import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, User, Loader2, UserPlus, Eye, EyeOff, ShieldCheck, Check, Fingerprint } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { toast } from '../hooks/use-toast';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { BrandLogo } from '../components/BrandLogo';
import AVATARS, { UserAvatar, VapeAvatar, avatarValue } from '../components/VapeAvatars';
import { isPasskeySupported, startPasskeyRegistration } from '../lib/passkey';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';

const strengthFor = (pw) => {
  let s = 0;
  if (pw.length >= 8) s++;
  if (pw.length >= 12) s++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) s++;
  if (/\d/.test(pw)) s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  return Math.min(s, 4);
};

const RegistroPage = () => {
  const navigate = useNavigate();
  const { setTokenAndUser } = useAuth();
  const [name, setName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [accepts, setAccepts] = useState(true);
  const [terms, setTerms] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedAvatar, setSelectedAvatar] = useState(
    () => AVATARS[Math.floor(Math.random() * AVATARS.length)].id
  );
  const [showSavePasskey, setShowSavePasskey] = useState(false);
  const [savingPasskey, setSavingPasskey] = useState(false);
  const passkeyOk = isPasskeySupported();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const googleLogin = () => {
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const s = strengthFor(password);
  const strengthLabel = ['', 'Muy débil', 'Débil', 'Buena', 'Fuerte'][s];
  const strengthColor = ['bg-white/10', 'bg-red-500', 'bg-orange-500', 'bg-yellow-400', 'bg-emerald-500'][s];

  const submit = async (e) => {
    e.preventDefault();
    if (!terms) { toast({ title: 'Acepta los términos', description: 'Debes aceptar los términos del servicio' }); return; }
    if (password !== confirm) { toast({ title: 'Las contraseñas no coinciden' }); return; }
    if (password.length < 8) { toast({ title: 'Contraseña muy corta', description: 'Mínimo 8 caracteres' }); return; }
    setLoading(true);
    try {
      const recaptchaToken = RECAPTCHA_SITE_KEY && executeRecaptcha
        ? await executeRecaptcha('register')
        : undefined;
      const r = await api.post('/auth/register', {
        email: email.trim().toLowerCase(),
        password,
        name: name.trim(),
        last_name: lastName.trim(),
        accepts_marketing: accepts,
        picture: avatarValue(selectedAvatar),
        ...(recaptchaToken ? { recaptcha_token: recaptchaToken } : {}),
      });
      setTokenAndUser(r.data.token, r.data.user);
      toast({ title: '¡Bienvenido a NubesMTY!', description: 'Tu cuenta está lista.' });
      if (passkeyOk) setShowSavePasskey(true);
      else navigate('/');
    } catch (err) {
      toast({ title: 'No pudimos crear la cuenta', description: err?.response?.data?.detail || 'Intenta de nuevo.' });
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[calc(100vh-160px)] flex items-center justify-center px-4 py-12 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-20 -left-20 w-[420px] h-[420px] rounded-full bg-orange-500/15 blur-[100px]" />
        <div className="absolute -bottom-20 -right-20 w-[380px] h-[380px] rounded-full bg-red-600/10 blur-[100px]" />
      </div>

      <div className="relative w-full max-w-md">
        <div className="text-center mb-8">
          <BrandLogo size="md" />
        </div>

        <div className="rounded-3xl border border-white/10 bg-gradient-to-b from-white/[0.04] to-transparent p-8 backdrop-blur">
          <h1 className="text-3xl md:text-4xl font-black text-white tracking-tight">
            Crea tu <span className="text-orange-400">cuenta</span>
          </h1>
          <p className="mt-2 text-white/60 text-sm">Solo necesitas correo y contraseña. Nada de códigos ni verificaciones raras.</p>

          <button
            type="button"
            onClick={googleLogin}
            className="mt-6 w-full h-12 rounded-full bg-white text-neutral-900 font-bold text-sm flex items-center justify-center gap-3 hover:bg-white/90 transition-colors shadow-md shadow-white/5"
            data-testid="register-google-btn"
          >
            <svg viewBox="0 0 24 24" className="w-5 h-5" aria-hidden>
              <path fill="#EA4335" d="M12 10.2v3.9h5.5c-.2 1.3-1.6 3.8-5.5 3.8-3.3 0-6-2.7-6-6s2.7-6 6-6c1.9 0 3.1.8 3.9 1.5l2.6-2.5C16.9 3.4 14.7 2.4 12 2.4 6.7 2.4 2.4 6.7 2.4 12S6.7 21.6 12 21.6c6.9 0 11.4-4.8 11.4-11.6 0-.8-.1-1.4-.2-2H12z"/>
            </svg>
            Registrarme con Google
          </button>

          <div className="mt-6 flex items-center gap-3 text-[11px] font-bold uppercase tracking-widest text-white/30">
            <div className="flex-1 h-px bg-white/10" /> o con correo <div className="flex-1 h-px bg-white/10" />
          </div>

          <form onSubmit={submit} className="mt-6 space-y-4" autoComplete="on" data-testid="register-form">

            {/* Avatar picker */}
            <div>
              <label className="text-[11px] font-bold uppercase tracking-wider text-white/50">Tu avatar</label>
              <div className="mt-2 flex items-center gap-3">
                <div className="flex-shrink-0">
                  <VapeAvatar id={selectedAvatar} size={52} />
                </div>
                <div className="flex flex-wrap gap-2">
                  {AVATARS.map((av) => (
                    <button
                      key={av.id}
                      type="button"
                      onClick={() => setSelectedAvatar(av.id)}
                      data-testid={`avatar-option-${av.id}`}
                      className={`rounded-full transition-all duration-150 ${
                        selectedAvatar === av.id
                          ? 'ring-2 ring-orange-500 ring-offset-2 ring-offset-black scale-110'
                          : 'opacity-60 hover:opacity-100'
                      }`}
                    >
                      <VapeAvatar id={av.id} size={32} />
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] font-bold uppercase tracking-wider text-white/50">Nombre</label>
                <div className="relative mt-2">
                  <User className="w-4 h-4 text-white/40 absolute left-3.5 top-1/2 -translate-y-1/2" />
                  <Input type="text" name="given-name" autoComplete="given-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Tu nombre" className="h-11 pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/40 focus-visible:ring-orange-500/50" data-testid="register-name-input" />
                </div>
              </div>
              <div>
                <label className="text-[11px] font-bold uppercase tracking-wider text-white/50">Apellido</label>
                <Input type="text" name="family-name" autoComplete="family-name" value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Apellido" className="h-11 mt-2 bg-white/5 border-white/10 text-white placeholder:text-white/40 focus-visible:ring-orange-500/50" data-testid="register-lastname-input" />
              </div>
            </div>

            <div>
              <label className="text-[11px] font-bold uppercase tracking-wider text-white/50">Correo electrónico</label>
              <div className="relative mt-2">
                <Mail className="w-4 h-4 text-white/40 absolute left-3.5 top-1/2 -translate-y-1/2" />
                <Input type="email" name="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="tu@correo.com" className="h-12 pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/40 focus-visible:ring-orange-500/50" data-testid="register-email-input" />
              </div>
            </div>

            <div>
              <label className="text-[11px] font-bold uppercase tracking-wider text-white/50">Contraseña</label>
              <div className="relative mt-2">
                <Lock className="w-4 h-4 text-white/40 absolute left-3.5 top-1/2 -translate-y-1/2" />
                <Input type={showPw ? 'text' : 'password'} name="new-password" autoComplete="new-password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Mínimo 8 caracteres" className="h-12 pl-10 pr-10 bg-white/5 border-white/10 text-white placeholder:text-white/40 focus-visible:ring-orange-500/50" data-testid="register-password-input" />
                <button type="button" onClick={() => setShowPw((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/80">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {password && (
                <div className="mt-2 flex items-center gap-2">
                  <div className="flex-1 h-1 bg-white/5 rounded-full overflow-hidden">
                    <div className={`h-full ${strengthColor} transition-all`} style={{ width: `${(s / 4) * 100}%` }} />
                  </div>
                  <span className="text-[10px] font-bold text-white/60 uppercase tracking-wider">{strengthLabel}</span>
                </div>
              )}
            </div>

            <div>
              <label className="text-[11px] font-bold uppercase tracking-wider text-white/50">Confirmar contraseña</label>
              <div className="relative mt-2">
                <ShieldCheck className="w-4 h-4 text-white/40 absolute left-3.5 top-1/2 -translate-y-1/2" />
                <Input type={showPw ? 'text' : 'password'} name="new-password" autoComplete="new-password" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Repite la contraseña" className="h-12 pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/40 focus-visible:ring-orange-500/50" data-testid="register-confirm-input" />
                {confirm && password === confirm && (
                  <Check className="w-4 h-4 text-emerald-400 absolute right-3.5 top-1/2 -translate-y-1/2" />
                )}
              </div>
            </div>

            <label className="flex items-start gap-2 text-xs text-white/60 cursor-pointer select-none">
              <input type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)} className="mt-0.5 accent-orange-500" data-testid="register-terms-check" />
              <span>Acepto los <Link to="/terminos-del-servicio" target="_blank" className="text-orange-400 hover:text-orange-300 underline">términos del servicio</Link> y confirmo ser mayor de 18 años.</span>
            </label>
            <label className="flex items-start gap-2 text-xs text-white/50 cursor-pointer select-none">
              <input type="checkbox" checked={accepts} onChange={(e) => setAccepts(e.target.checked)} className="mt-0.5 accent-orange-500" />
              <span>Quiero recibir ofertas y novedades por correo.</span>
            </label>

            <Button type="submit" disabled={loading} className="w-full h-13 h-14 rounded-full bg-orange-500 hover:bg-orange-600 text-black font-bold text-base shadow-lg shadow-orange-500/25" data-testid="register-submit-btn">
              {loading ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Creando cuenta…</> : <><UserPlus className="w-4 h-4 mr-2" /> Crear cuenta</>}
            </Button>
          </form>

          <p className="text-center mt-6 text-sm text-white/60">
            ¿Ya tienes cuenta? <Link to="/inicio-sesion" className="text-orange-400 font-bold hover:text-orange-300">Inicia sesión</Link>
          </p>
        </div>
      </div>

      {/* Save passkey prompt after registration */}
      {showSavePasskey && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="w-full max-w-sm rounded-3xl border border-orange-500/30 bg-[#111] p-6 shadow-2xl shadow-orange-500/10">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center flex-shrink-0">
                <Fingerprint className="w-5 h-5 text-orange-400" />
              </div>
              <div>
                <p className="text-white font-bold text-sm">Acceso rápido con tu huella</p>
                <p className="text-white/50 text-xs">Guarda una llave de acceso para entrar sin contraseña la próxima vez</p>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button
                onClick={async () => {
                  setSavingPasskey(true);
                  try {
                    const device = navigator.userAgent.includes('iPhone') ? 'iPhone' : navigator.userAgent.includes('Android') ? 'Android' : 'Mi dispositivo';
                    await startPasskeyRegistration(api, device);
                    toast({ title: 'Llave guardada', description: 'La próxima vez entra con tu huella.' });
                  } catch (err) {
                    if (err?.name !== 'NotAllowedError') toast({ title: 'No se pudo guardar', description: err?.message });
                  } finally {
                    setSavingPasskey(false);
                    navigate('/');
                  }
                }}
                disabled={savingPasskey}
                className="flex-1 bg-orange-500 hover:bg-orange-600 text-black font-bold rounded-full"
                data-testid="register-save-passkey-btn"
              >
                {savingPasskey ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Guardar llave'}
              </Button>
              <Button
                onClick={() => navigate('/')}
                variant="ghost"
                className="flex-1 text-white/60 hover:text-white rounded-full"
                data-testid="register-skip-passkey-btn"
              >
                Ahora no
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default function RegistroPageExport() {
  if (!RECAPTCHA_SITE_KEY) return <RegistroPage />;
  return (
    <GoogleReCaptchaProvider reCaptchaKey={RECAPTCHA_SITE_KEY}>
      <RegistroPage />
    </GoogleReCaptchaProvider>
  );
}
